<script>
    // The routine rating, as the reco-engine returns it (/api/routine-analyze).
    //
    // Score out of 100 from four parts: fit to your skin (40), essentials (25),
    // safety (25), simplicity (10). Confidence is shown beside the score and
    // never inside it: a product we could only read by name lowers confidence,
    // not the score.
    //
    // If the engine is down the website answers with the older name-based
    // checks (engine: 'fallback'). That shape is mapped onto this one so the
    // screen never breaks, and says the rating is a quick check.
    import Card from './Card.svelte';
    import Chip from './Chip.svelte';
    import Banner from './Banner.svelte';

    export let rating;

    const PART_LABELS = {
        fit: 'Fit for your skin',
        essentials: 'Essentials',
        safety: 'Safety',
        simplicity: 'Simplicity'
    };
    const PART_ORDER = ['fit', 'essentials', 'safety', 'simplicity'];

    $: view = normalize(rating);

    function normalize(r) {
        if (!r) return null;
        if (r.engine !== 'fallback' && r.verdict !== undefined) return r;
        // Older name-based checks.
        const fixes = [
            ...(r.missing || []).map(m => ({ title: `Add ${m.title.toLowerCase()}`, body: m.body, kind: 'essentials' })),
            ...(r.concerns || []).map(c => ({ title: c.title, body: c.body, kind: 'safety' })),
            ...(r.adjust || []).map(a => ({ title: a.title, body: a.body, kind: 'timing' }))
        ];
        const b = r.scoreBreakdown || {};
        return {
            score: r.score,
            verdict: r.nextAction || '',
            parts: { essentials: b.essentials, safety: b.safety, simplicity: b.simplicity },
            confidence: { level: 'low', unread: [] },
            coverage: [],
            fixes: fixes.slice(0, 3),
            warnings: [],
            timingPlan: r.timingPlan || [],
            quickCheck: true
        };
    }

    function tone(value) {
        if (value >= 80) return 'success';
        if (value >= 60) return 'caution';
        return 'danger';
    }

    function fixTone(kind) {
        if (kind === 'safety' || kind === 'prescription') return 'danger';
        if (kind === 'essentials') return 'caution';
        return 'default';
    }

    const CONFIDENCE_COPY = {
        high: 'High confidence. We read every product’s ingredients.',
        medium: 'Medium confidence. Some products were read from their names only.',
        low: 'Low confidence. Most products were read from their names only.'
    };

    function coverageLabel(value) {
        if (value >= 0.9) return 'Well covered';
        if (value >= 0.64) return 'Covered';
        if (value >= 0.36) return 'Partly covered';
        return 'Not covered';
    }
</script>

{#if view}
<section class="rating" aria-labelledby="rating-title">
    <div class="top">
        <div class="score" aria-label="Routine score {view.score} out of 100">
            <span class="num">{view.score}</span><span class="of">/100</span>
        </div>
        <div class="copy">
            <p class="eyebrow">Your routine, rated for your skin</p>
            <h2 id="rating-title">{view.verdict}</h2>
            {#if view.quickCheck}
                <p class="muted">A quick check. The full rating is unavailable right now, so this one
                    reads product names only.</p>
            {:else if view.confidence}
                <p class="muted">{CONFIDENCE_COPY[view.confidence.level] || ''}
                    {#if view.confidence.unread && view.confidence.unread.length}
                        Match {view.confidence.unread.slice(0, 3).join(', ')} to the catalog for a sharper rating.
                    {/if}
                </p>
            {/if}
        </div>
    </div>

    <div class="parts">
        {#each PART_ORDER as key}
            {#if view.parts && view.parts[key] !== undefined && view.parts[key] !== null}
                <div class="part">
                    <div class="part-head">
                        <span>{PART_LABELS[key]}</span>
                        <span class="part-value">{view.parts[key]}</span>
                    </div>
                    <div class="meter" aria-hidden="true">
                        <span class={tone(view.parts[key])} style="width: {Math.max(4, view.parts[key])}%"></span>
                    </div>
                </div>
            {/if}
        {/each}
    </div>
    {#if view.fitRated === false}
        <p class="muted small">Fit is left out because we don’t have a skin analysis for you yet.</p>
    {/if}

    {#if view.warnings && view.warnings.length}
        {#each view.warnings as w}
            <div class="warning">
                <Banner tone="caution" title={w.title}>{w.body}</Banner>
            </div>
        {/each}
    {/if}

    {#if view.fixes && view.fixes.length}
        <h3 class="sub">What to change first</h3>
        <ol class="fixes">
            {#each view.fixes as fix, i}
                <li>
                    <Card tone={fixTone(fix.kind)}>
                        <p class="fix-num">{i + 1}</p>
                        <p class="fix-title">{fix.title}</p>
                        <p class="muted">{fix.body}</p>
                    </Card>
                </li>
            {/each}
        </ol>
    {:else}
        <p class="muted">Nothing to change. Keep it steady and check back after your next photo.</p>
    {/if}

    {#if view.coverage && view.coverage.length}
        <details class="more">
            <summary>How it covers your concerns</summary>
            <ul class="coverage">
                {#each view.coverage as c}
                    <li>
                        <span class="c-name">{c.label}</span>
                        <Chip tone={c.coverage >= 0.64 ? 'success' : c.coverage >= 0.36 ? 'caution' : 'danger'}>{coverageLabel(c.coverage)}</Chip>
                        {#if c.treatedBy && c.treatedBy.length}
                            <span class="muted small">by {c.treatedBy.join(', ')}</span>
                        {/if}
                    </li>
                {/each}
            </ul>
        </details>
    {/if}

    {#if view.timingPlan && view.timingPlan.length}
        <details class="more">
            <summary>When to use what</summary>
            <ul class="timing">
                {#each view.timingPlan as t}
                    <li><strong>{t.title}</strong>: {t.body}</li>
                {/each}
            </ul>
        </details>
    {/if}

    <p class="note">Guidance on routines, not medical advice. If a product was prescribed to you,
        check any change with whoever prescribed it.</p>
</section>
{/if}

<style>
    .rating {
        font-family: var(--font-body);
    }
    .top {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: var(--space-6);
        margin-bottom: var(--space-8);
    }
    .score {
        flex: none;
        font-family: var(--font-display);
        line-height: var(--leading-tight);
        color: var(--sage);
    }
    .num { font-size: var(--text-5xl); }
    .of { font-size: var(--text-lg); color: var(--text-muted); margin-left: var(--space-1); }
    .copy { flex: 1 1 280px; }
    .eyebrow {
        color: var(--sage);
        font-size: var(--text-2xs);
        font-weight: var(--weight-medium);
        letter-spacing: var(--track-eyebrow);
        text-transform: uppercase;
        margin: 0 0 var(--space-2);
    }
    h2 {
        font-family: var(--font-display);
        font-weight: var(--weight-regular);
        font-size: var(--text-2xl);
        line-height: var(--leading-snug);
        margin: 0 0 var(--space-3);
        color: var(--text);
    }
    .muted { color: var(--text-muted); line-height: var(--leading-relaxed); margin: 0; }
    .small { font-size: var(--text-sm); }
    .parts {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
        gap: var(--space-5);
        padding: var(--space-6) 0;
        border-top: 1px solid var(--border);
        border-bottom: 1px solid var(--border);
        margin-bottom: var(--space-4);
    }
    .part-head {
        display: flex;
        justify-content: space-between;
        font-size: var(--text-sm);
        color: var(--text-muted);
        margin-bottom: var(--space-2);
    }
    .part-value { color: var(--text); font-weight: var(--weight-medium); }
    .meter {
        height: 6px;
        border-radius: var(--radius-pill);
        background: var(--surface-3);
        overflow: hidden;
    }
    .meter span { display: block; height: 100%; border-radius: inherit; }
    .meter .success { background: var(--success); }
    .meter .caution { background: var(--caution); }
    .meter .danger { background: var(--danger); }
    .warning { margin: var(--space-4) 0; }
    .sub {
        font-size: var(--text-2xs);
        font-weight: var(--weight-medium);
        letter-spacing: var(--track-label);
        text-transform: uppercase;
        color: var(--text-muted);
        margin: var(--space-8) 0 var(--space-4);
    }
    .fixes {
        list-style: none;
        padding: 0;
        margin: 0;
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
        gap: var(--space-3);
    }
    .fix-num {
        font-family: var(--font-display);
        color: var(--sage);
        font-size: var(--text-lg);
        margin: 0;
    }
    .fix-title {
        font-weight: var(--weight-medium);
        color: var(--text);
        margin: var(--space-1) 0 var(--space-2);
    }
    .more {
        margin-top: var(--space-6);
        border-top: 1px solid var(--border);
        padding-top: var(--space-4);
    }
    .more summary {
        cursor: pointer;
        color: var(--text);
        font-weight: var(--weight-medium);
    }
    .coverage, .timing {
        list-style: none;
        padding: 0;
        margin: var(--space-4) 0 0;
        display: grid;
        gap: var(--space-3);
    }
    .coverage li {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: var(--space-3);
    }
    .c-name { min-width: 8rem; color: var(--text); text-transform: capitalize; }
    .timing li { color: var(--text-muted); line-height: var(--leading-relaxed); }
    .timing strong { color: var(--text); font-weight: var(--weight-medium); }
    .note {
        margin-top: var(--space-6);
        font-size: var(--text-sm);
        color: var(--text-muted);
    }
</style>
